// F11 (#3988) — first reachability-probe gate.
//
// OnlineStateService boots with _connectionState = Offline and only flips it
// after the first async ping resolves. assureInitialSyncAsync reads that state;
// if it runs before the first ping, a genuinely-online cold boot can read the
// Offline placeholder and silently skip its initial sync. This gate lets a
// caller wait (bounded) for that first probe to complete before reading the
// state.
//
// The race logic lives here as a pure function so it is node-testable —
// OnlineStateService itself cannot be constructed under node (it wires window
// event listeners in its constructor).

import type { ClearTimeoutFn, ScheduleTimeout } from './local-store-timeout';

/** Which side of the race settled first. Returned so the caller can surface a
 * Timeout (the probe did not complete in time and the caller may read a stale
 * state) instead of silently proceeding. */
export enum FirstProbeGateOutcome {
  Signal = 'signal',
  Timeout = 'timeout',
}

/** What awaitFirstProbeGateAsync needs from OnlineStateService, passed in so a test can fake it. */
export interface FirstProbeGatePorts {
  /** resolves once the first connection check has finished */
  firstProbeDone: Promise<unknown>;
  timeoutMs: number;
  /** the timer the limit is measured with */
  timer: { schedule: ScheduleTimeout, clear: ClearTimeoutFn };
  /** how long ago a connection check wrote its verdict into the connection state;
   * Infinity when none has yet, so the state still holds the boot placeholder */
  msSinceVerdict(): number;
  /** the connection state the callers will read once the gate opens */
  connectionState(): string;
  reportProblem(name: string, context: Record<string, unknown>): void;
  reportInfo(name: string, context: Record<string, unknown>): void;
}

/** The first-probe gate itself (#3988 F11): wait for the first connection check, at most
 * `timeoutMs`, and report when the limit won. Pure so a node test can drive it — the service
 * that owns the check cannot be built under node. The timer is cancelled once the check wins. */
export async function awaitFirstProbeGateAsync(ports: FirstProbeGatePorts): Promise<void> {
  let timeoutHandle: unknown;
  let outcome: FirstProbeGateOutcome;
  try {
    outcome = await Promise.race([
      Promise.resolve(ports.firstProbeDone).then(() => FirstProbeGateOutcome.Signal),
      new Promise<FirstProbeGateOutcome>(resolve => {
        timeoutHandle = ports.timer.schedule(() => resolve(FirstProbeGateOutcome.Timeout), ports.timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutHandle !== undefined) ports.timer.clear(timeoutHandle);
  }
  if (outcome !== FirstProbeGateOutcome.Timeout) return;

  // #6121: the check lets go only after it has also refreshed the CouchDB login (an
  // Offline -> Online flip waits for Firebase and the login), so the limit can win with a
  // verdict already in the state. The caller then reads that verdict, not the placeholder:
  // nothing is wrong, only slow. Keep it measurable without posing as a problem.
  const msSinceVerdict = ports.msSinceVerdict();
  if (msSinceVerdict !== Infinity) {
    ports.reportInfo('OnlineState: first-probe gate outlasted the login refresh', {
      timeoutMs: ports.timeoutMs,
      msSinceVerdict,
      connectionState: ports.connectionState(),
    });
    return;
  }

  // if no check has written a verdict, the caller is about to read the possibly-stale
  // Offline placeholder — the very race this gate exists to close. Surface it.
  ports.reportProblem('OnlineState: first-probe gate timed out', { timeoutMs: ports.timeoutMs });
}

/** Resolve when `signal` settles OR after `timeoutMs`, whichever comes first,
 * reporting which one won. The timeout is a hard ceiling so a hung probe can
 * never stall the caller; a probe that completes normally wins immediately (the
 * common case, zero added delay). Takes `sleep` injected so a test can drive it
 * with a fake. A losing (rejected) `sleep` never surfaces — Promise.race
 * attaches a handler to it, so its rejection is observed and ignored once the
 * signal has already won. */
export async function awaitSignalOrTimeoutAsync(
  signal: Promise<unknown>,
  sleep: (ms: number) => Promise<unknown>,
  timeoutMs: number,
): Promise<FirstProbeGateOutcome> {
  return Promise.race([
    Promise.resolve(signal).then(() => FirstProbeGateOutcome.Signal),
    sleep(timeoutMs).then(() => FirstProbeGateOutcome.Timeout),
  ]);
}
